<template>
    <v-menu v-model="runningAppsMenuOpen" offset-y>
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip bottom>
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-btn
                        v-on="{ ...menuOn, ...tooltipOn }"
                        class="px-3"
                        min-width="0"
                        :input-value="runningAppsMenuOpen"
                        active-class="primary--text"
                        e2e-toolbar-running-apps>
                        <v-badge v-if="menuDeployments.length" :content="menuDeployments.length" color="primary" overlap>
                            <v-icon small>mdi-monitor</v-icon>
                        </v-badge>
                        <v-icon small v-else>mdi-monitor</v-icon>
                    </v-btn>
                </template>
                <span>Applications</span>
            </v-tooltip>
        </template>
        <v-list dense min-width="200">
            <v-list-item two-line>
                <v-list-item-content>
                    <v-list-item-title class="secondary--text subtitle-1 font-weight-bold">{{ pushUiEnabled ? 'Applications' : 'Running' }}</v-list-item-title>
                    <v-list-item-subtitle>
                        <template v-if="pushUiEnabled">
                            {{ runningDeployments.length }} running
                            <span v-if="nonReadyCount">· {{ nonReadyCount }} not ready</span>
                        </template>
                        <template v-else>{{ runningDeployments.length }} applications</template>
                    </v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
            <v-divider v-if="menuDeployments.length" class="mt-3 mb-3"></v-divider>
            <v-list-item v-if="pushUiEnabled && !menuDeployments.length" class="secondary--text text--lighten-2">
                <v-list-item-icon class="mr-3">
                    <v-icon small color="grey">mdi-monitor-off</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title>No active applications</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-list-item-group v-else color="secondary">
                <v-tooltip bottom v-for="app in menuDeployments" :key="app.aid">
                    <template v-slot:activator="{ on }">
                        <v-list-item v-on="on" :to="{ name: 'app-open', params: { oid: app.oid, sid: app.sid, iid: app.iid, snid: app.snid, aid: app.aid } }">
                            <v-list-item-avatar>
                                <img :src="appIcon(app)" />
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title class="d-flex align-center">
                                    <span
                                        v-if="pushUiEnabled"
                                        aria-hidden="true"
                                        class="status-dot mr-2 flex-shrink-0"
                                        :class="`status-dot--${statusInfo(app).state.toLowerCase()}`"></span>
                                    <h4 class="secondary--text mr-2">{{ app.name }}</h4>
                                    <v-chip
                                        v-if="pushUiEnabled && statusInfo(app).state !== 'RUNNING'"
                                        x-small
                                        :color="statusInfo(app).color"
                                        text-color="white"
                                        class="mr-1">
                                        {{ statusInfo(app).label }}
                                    </v-chip>
                                    <v-chip v-if="app.shared === 'true'" small outlined>shared</v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle>{{ app.description }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <stop-application :app="app" :appName="app.name" :isShared="app.shared === 'true'" :sessionId="app.session_id">
                                    <v-btn color="secondary" icon>
                                        <v-icon dark>mdi-stop</v-icon>
                                    </v-btn>
                                </stop-application>
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                    <div class="d-flex flex-column caption">
                        <span>
                            Space:
                            <span class="font-weight-bold text-uppercase">{{ app.space_long_id }}</span>
                        </span>
                        <span>
                            Instance:
                            <span class="font-weight-bold text-uppercase">{{ app.instance_long_id }}</span>
                        </span>
                    </div>
                </v-tooltip>
            </v-list-item-group>
        </v-list>

        <v-divider></v-divider>
    </v-menu>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'
import { pushUiMixin } from '@/mixins/pushUi'
import { deploymentStatusInfo } from '@/modules/application/deploymentStatus'

const StopApplication = () => import('@/modules/application/components/TheApplicationStopDialog.vue')

export default {
    components: {
        StopApplication
    },
    mixins: [appTypeAndImageLink, pushUiMixin],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            runningAppsMenuOpen: false
        }
    },
    methods: {
        statusInfo(app) {
            return deploymentStatusInfo(app)
        }
    },
    computed: {
        ...mapGetters('appStore', ['activeDeployments', 'runningDeployments']),
        ...mapState(['userInfo']),
        menuDeployments() {
            // Pre-push FE only listed running apps; enhanced menu includes starting/unavailable.
            if (!this.pushUiEnabled) return this.runningDeployments || []
            return this.activeDeployments || []
        },
        nonReadyCount() {
            if (!this.pushUiEnabled) return 0
            return this.menuDeployments.length - this.runningDeployments.length
        }
    },
    watch: {
        userInfo: {
            async handler(user) {
                if (user) {
                    this.$store.dispatch('userStore/fetchRunningUserTasks')
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
}
.status-dot--running {
    background-color: #4caf50;
}
.status-dot--starting {
    background-color: #ff7043;
    animation: statusDotPulse 1.2s ease-in-out infinite;
}
.status-dot--unavailable {
    background-color: #f44336;
}
.status-dot--stopping {
    background-color: #607d8b;
}
@keyframes statusDotPulse {
    0%,
    100% {
        opacity: 0.4;
        transform: scale(0.85);
    }
    50% {
        opacity: 1;
        transform: scale(1.2);
    }
}
@media (prefers-reduced-motion: reduce) {
    .status-dot--starting {
        animation: none;
    }
}
</style>
